import { useState, useRef, useEffect } from "react";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface DateInputProps {
    id?: string;
    value?: string;
    onChange: (date: string | undefined) => void;
    placeholder?: string;
    readOnly?: boolean;
    error?: string;
    className?: string;
    disabled?: boolean;
}

export function DateInput({
    id,
    value,
    onChange,
    readOnly,
    error,
    className,
    disabled,
}: DateInputProps) {
    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");
    const [day, setDay] = useState("");

    const yearRef = useRef<HTMLInputElement>(null);
    const monthRef = useRef<HTMLInputElement>(null);
    const dayRef = useRef<HTMLInputElement>(null);
    const dateInputRef = useRef<HTMLInputElement>(null);
    const lastValueRef = useRef<string | undefined>(value);

    // Only sync from external value changes (not from our own onChange calls)
    useEffect(() => {
        if (value !== lastValueRef.current) {
            lastValueRef.current = value;
            if (value) {
                const [y, m, d] = value.split("-");
                setYear(y || "");
                setMonth(m || "");
                setDay(d || "");
            } else {
                setYear("");
                setMonth("");
                setDay("");
            }
        }
    }, [value]);

    const emitChange = (y: string, m: string, d: string) => {
        if (y.length === 4 && m.length === 2 && d.length === 2) {
            const isoString = `${y}-${m}-${d}`;
            lastValueRef.current = isoString;
            onChange(isoString);
        } else if (!y && !m && !d) {
            lastValueRef.current = undefined;
            onChange(undefined);
        }
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, "").slice(0, 4);
        setYear(val);
        if (val.length === 4) {
            monthRef.current?.focus();
        }
        emitChange(val, month, day);
    };

    const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, "").slice(0, 2);
        setMonth(val);
        if (val.length === 2) {
            dayRef.current?.focus();
        }
        emitChange(year, val, day);
    };

    const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, "").slice(0, 2);
        setDay(val);
        emitChange(year, month, val);
    };

    const openCalendar = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (dateInputRef.current) {
            dateInputRef.current.style.pointerEvents = 'auto';
            dateInputRef.current.focus();
            try {
                dateInputRef.current.showPicker();
            } catch {
                dateInputRef.current.click();
            }
        }
    };

    const handleCalendarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val) {
            const [y, m, d] = val.split("-");
            setYear(y);
            setMonth(m);
            setDay(d);
            lastValueRef.current = val;
            onChange(val);
        }
        // Reset pointer events after selection
        if (dateInputRef.current) {
            dateInputRef.current.style.pointerEvents = 'none';
        }
    };

    return (
        <div className="w-full">
            <div className="relative flex items-center">
                <div
                    className={cn(
                        "flex items-center gap-0 h-10 px-2.5 pr-10 rounded-[var(--color-radius)] border border-md-outline bg-md-surface text-md-on-surface text-sm flex-1 transition-all",
                        "hover:border-md-primary hover:bg-md-tertiary-container/10",
                        "focus-within:border-md-primary focus-within:ring-[3px] focus-within:ring-md-primary/20",
                        disabled && "pointer-events-none cursor-not-allowed bg-md-surface-variant text-md-on-surface-variant border-md-outline-variant",
                        error && "border-red-500",
                        className,
                    )}
                >
                    <input
                        id={id}
                        ref={yearRef}
                        type="text"
                        value={year}
                        onChange={handleYearChange}
                        placeholder="YYYY"
                        disabled={disabled || readOnly}
                        onFocus={(e) => e.target.select()}
                        className="w-[2.5rem] bg-transparent outline-none placeholder:text-gray-600 text-center"
                    />
                    <span className="text-gray-400 mx-0.5">-</span>
                    <input
                        ref={monthRef}
                        type="text"
                        value={month}
                        onChange={handleMonthChange}
                        placeholder="MM"
                        disabled={disabled || readOnly}
                        onFocus={(e) => e.target.select()}
                        className="w-[1.5rem] bg-transparent outline-none placeholder:text-gray-600 text-center"
                    />
                    <span className="text-gray-400 mx-0.5">-</span>
                    <input
                        ref={dayRef}
                        type="text"
                        value={day}
                        onChange={handleDayChange}
                        placeholder="DD"
                        disabled={disabled || readOnly}
                        onFocus={(e) => e.target.select()}
                        className="w-[1.5rem] bg-transparent outline-none placeholder:text-gray-600 text-center"
                    />
                </div>
                <input
                    ref={dateInputRef}
                    type="date"
                    onChange={handleCalendarChange}
                    value={
                        year && month && day ? `${year}-${month}-${day}` : ""
                    }
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    tabIndex={-1}
                    style={{ pointerEvents: 'none' }}
                />
                <button
                    type="button"
                    onClick={openCalendar}
                    onTouchStart={openCalendar}
                    disabled={disabled || readOnly}
                    className="absolute right-2 p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed z-10"
                >
                    <CalendarIcon className="size-4" />
                </button>
            </div>
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
    );
}
